/**
 * 67.30 — shooting stars (the owner, Oct 10: "shooting star animation in sky sometimes if cheap, can be tied into
 * 'biome' / characterization of place (wrt space in this case)"). Now and then a short streak crosses the sky with a
 * fading tail — no light, nothing in the sim. Where the stars show (rendering/skyHaze.ts: in space, or inside an
 * atmosphere at night) the streaks show by as much; by day they are gone with the stars.
 *
 * The place sets the rate (`skyCharacter`): a crowded system more, an empty one rarely, a ring's debris belt more
 * again; and now and then a shower, on a schedule seeded from the match, when they come several times as often.
 *
 * Cheap: a pool of four 1-pixel lines (two vertices and a colour each), drawn only while one is falling — no draws
 * otherwise, no allocation per frame.
 */
import * as THREE from 'three';
import type { GameState } from '../types';

/** The numbers: streaks a minute at an ordinary system, the pool, how far out they are drawn (world units from the camera), each streak's life (s) and arc (radians), the colour, the showers. */
export const SHOOTING = {
  perMinute: 1.2, pool: 4, distance: 150,
  life: [0.45, 1.0] as const, arc: [0.12, 0.3] as const, tail: 0.35,
  color: new THREE.Color('#dfe8ff'),
  /** a shower: every `every` seconds (a seeded phase), for `lasts` seconds, `times` the rate */
  shower: { every: 240, lasts: 25, times: 8 },
};

/** The sky's character from the place: how many streaks a minute. */
export function skyCharacter(state: Pick<GameState, 'planets' | 'environmentalEffects'>): number {
  const bodies = state.planets.filter(p => p.type !== 'star' && p.type !== 'wormhole' && p.alive !== false).length;
  const crowd = bodies <= 2 ? 0.4 : bodies <= 5 ? 1 : 1.6;
  const debris = (state.environmentalEffects ?? []).some(e => e.effectType === 'planetRings' && e.alive) ? 2.5 : 1;
  return SHOOTING.perMinute * crowd * debris;
}

/** A small seeded generator (mulberry32 over the seed's hash). */
function rng(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  let s = h || 1;
  return () => { s = (s + 0x6d2b79f5) >>> 0; let t = s; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

interface Streak { line: THREE.Line; pos: THREE.BufferAttribute; col: THREE.BufferAttribute; age: number; life: number; from: THREE.Vector3; axis: THREE.Vector3; arc: number }

export class ShootingStars {
  private readonly pool: Streak[] = [];
  private readonly next: () => number;
  private untilNext: number;
  private time = 0;
  private readonly showerPhase: number;
  private readonly _a = new THREE.Vector3();
  private readonly _b = new THREE.Vector3();
  private readonly _q = new THREE.Quaternion();

  constructor(private readonly scene: THREE.Scene, seed: string) {
    this.next = rng(`${seed}#shooting`);
    this.showerPhase = this.next() * SHOOTING.shower.every;
    this.untilNext = 5 + this.next() * 20;
    for (let i = 0; i < SHOOTING.pool; i++) {
      const g = new THREE.BufferGeometry();
      const pos = new THREE.BufferAttribute(new Float32Array(6), 3), col = new THREE.BufferAttribute(new Float32Array(8), 4);
      g.setAttribute('position', pos); g.setAttribute('color', col);
      const line = new THREE.Line(g, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, fog: false, blending: THREE.AdditiveBlending }));
      line.frustumCulled = false; line.visible = false; line.renderOrder = 0;
      scene.add(line);
      this.pool.push({ line, pos, col, age: 0, life: 0, from: new THREE.Vector3(), axis: new THREE.Vector3(), arc: 0 });
    }
  }

  /** Whether a shower is falling at `t` seconds. */
  inShower(t = this.time): boolean {
    return (t + this.showerPhase) % SHOOTING.shower.every < SHOOTING.shower.lasts;
  }

  /**
   * One frame: `shown` how far the stars show (0 by day under an atmosphere … 1 in space or at night), `up` the local
   * up at the camera (null in open space: any direction), `perMinute` the place's rate (`skyCharacter`).
   */
  update(camera: THREE.Camera, shown: number, up: THREE.Vector3 | null, perMinute: number, dt: number): void {
    this.time += dt;
    // spawn: a Poisson clock, faster in a shower; only where the stars show
    this.untilNext -= dt * (this.inShower() ? SHOOTING.shower.times : 1);
    if (this.untilNext <= 0) {
      this.untilNext = -Math.log(Math.max(1e-6, 1 - this.next())) * (60 / Math.max(1e-3, perMinute));
      if (shown > 0.05) this.spawn(up);
    }
    for (const s of this.pool) {
      if (!s.line.visible) continue;
      s.age += dt;
      const k = s.age / s.life;
      if (k >= 1) { s.line.visible = false; continue; }
      // the head moves along its arc; the tail trails it, longest mid-flight
      const head = this._a.copy(s.from).applyQuaternion(this._q.setFromAxisAngle(s.axis, s.arc * k));
      const tail = this._b.copy(s.from).applyQuaternion(this._q.setFromAxisAngle(s.axis, s.arc * Math.max(0, k - SHOOTING.tail * Math.sin(Math.PI * Math.min(1, k * 1.4)))));
      const cam = camera.position;
      s.pos.setXYZ(0, cam.x + head.x * SHOOTING.distance, cam.y + head.y * SHOOTING.distance, cam.z + head.z * SHOOTING.distance);
      s.pos.setXYZ(1, cam.x + tail.x * SHOOTING.distance, cam.y + tail.y * SHOOTING.distance, cam.z + tail.z * SHOOTING.distance);
      s.pos.needsUpdate = true;
      const bright = shown * Math.min(1, k / 0.12) * Math.min(1, (1 - k) / 0.3);
      const c = SHOOTING.color;
      s.col.setXYZW(0, c.r, c.g, c.b, bright);
      s.col.setXYZW(1, c.r, c.g, c.b, 0);
      s.col.needsUpdate = true;
    }
  }

  /** Streaks falling now (for tests and the F3 overlay). */
  falling(): number { return this.pool.filter(s => s.line.visible).length; }

  private spawn(up: THREE.Vector3 | null): void {
    const s = this.pool.find(p => !p.line.visible);
    if (!s) return;
    const r = this.next;
    // a start above the local horizon (20°–75° up), or anywhere in open space; a travel axis across it
    const u = up ? up.clone().normalize() : new THREE.Vector3(r() - 0.5, r() - 0.5, r() - 0.5).normalize();
    const side = new THREE.Vector3(r() - 0.5, r() - 0.5, r() - 0.5).cross(u).normalize();
    if (side.lengthSq() < 1e-9) side.set(1, 0, 0);
    const elev = (20 + r() * 55) * Math.PI / 180, az = r() * Math.PI * 2;
    const fwd = side.clone().applyAxisAngle(u, az);
    s.from.copy(u).multiplyScalar(Math.sin(elev)).addScaledVector(fwd, Math.cos(elev)).normalize();
    // the axis it falls about: mostly downward across the sky (toward the horizon), a little sideways
    const down = u.clone().addScaledVector(s.from, -u.dot(s.from)).negate().normalize();
    const across = new THREE.Vector3().crossVectors(s.from, down).normalize();
    const dir = down.multiplyScalar(0.7 + 0.3 * r()).addScaledVector(across, (r() - 0.5) * 0.8).normalize();
    s.axis.crossVectors(s.from, dir).normalize();
    s.arc = SHOOTING.arc[0] + r() * (SHOOTING.arc[1] - SHOOTING.arc[0]);
    s.life = SHOOTING.life[0] + r() * (SHOOTING.life[1] - SHOOTING.life[0]);
    s.age = 0;
    s.line.visible = true;
  }

  dispose(): void {
    for (const s of this.pool) { this.scene.remove(s.line); s.line.geometry.dispose(); (s.line.material as THREE.Material).dispose(); }
    this.pool.length = 0;
  }
}
